import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PlusIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChartBarIcon,
  ClockIcon,
  CurrencyPoundIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { projectAPI } from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';

// Status badge component
function StatusBadge({ status }) {
  const colors = {
    planning: 'bg-gray-100 text-gray-800',
    active: 'bg-blue-100 text-blue-800',
    on_hold: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || colors.planning}`}>
      {status.replace('_', ' ').toUpperCase()}
    </span>
  );
}

// Priority badge component
function PriorityBadge({ priority }) {
  const colors = {
    low: 'bg-gray-100 text-gray-600',
    medium: 'bg-blue-100 text-blue-600',
    high: 'bg-orange-100 text-orange-600',
    critical: 'bg-red-100 text-red-600',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[priority] || colors.medium}`}>
      {priority?.toUpperCase() || 'MEDIUM'}
    </span>
  );
}

// Project card component
function ProjectCard({ project }) {
  const budgetUsed = project.budget > 0 ? (project.spent / project.budget * 100).toFixed(0) : 0;

  return (
    <Link
      to={`/projects/${project.project_id}`}
      className="block bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {project.title}
          </h3>
          <p className="text-sm text-gray-600 line-clamp-2">
            {project.description || project.impact_statement}
          </p>
        </div>
        <FolderIcon className="w-6 h-6 text-gray-400 ml-4" />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <StatusBadge status={project.status} />
        <PriorityBadge priority={project.priority} />
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="flex items-center text-gray-600">
          <ChartBarIcon className="w-4 h-4 mr-2" />
          <span>{project.overall_progress || 0}% Complete</span>
        </div>
        <div className="flex items-center text-gray-600">
          <UsersIcon className="w-4 h-4 mr-2" />
          <span>{project.team_member_ids?.length || 0} Members</span>
        </div>
        <div className="flex items-center text-gray-600">
          <CurrencyPoundIcon className="w-4 h-4 mr-2" />
          <span>£{(project.budget || 0).toLocaleString()}</span>
        </div>
        <div className="flex items-center text-gray-600">
          <ClockIcon className="w-4 h-4 mr-2" />
          <span>{budgetUsed}% Spent</span>
        </div>
      </div>

      {project.start_date && (
        <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
          Started: {new Date(project.start_date).toLocaleDateString('en-GB')}
        </div>
      )}
    </Link>
  );
}

export default function Projects() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch projects from API
  const { data: projects, isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectAPI.getAll(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <FolderIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading projects</h3>
        <p className="mt-1 text-sm text-gray-500">{error.message}</p>
      </div>
    );
  }

  // Filter projects
  const filteredProjects = projects?.filter(project => {
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    const matchesSearch = !searchQuery ||
      project.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  }) || [];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-gray-600">Manage and monitor all your AI-powered projects</p>
        </div>
        <Link
          to="/projects/new"
          className="btn-primary flex items-center space-x-2"
        >
          <PlusIcon className="w-4 h-4" />
          <span>New Project</span>
        </Link>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No projects found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {projects?.length === 0
              ? "Get started by creating your first project."
              : "Try adjusting your filters or search query."}
          </p>
          <div className="mt-6">
            <Link
              to="/projects/new"
              className="btn-primary inline-flex items-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Project
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Showing {filteredProjects.length} of {projects?.length || 0} projects</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <ProjectCard key={project.project_id} project={project} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
